import React, { useState } from 'react';
import { EventItem, LanguageCode } from '../types';
import { translations } from '../i18n/translations';
import { getLocalized, getLocalizedCategory } from '../utils/i18n';
import { MapPin, Clock, Users, QrCode, CheckCircle2, Download, X, Share2, Printer, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { registerEventApi } from '../services/api';

interface EventsViewProps {
  currentLang: LanguageCode;
  events: EventItem[];
  selectedEventItem?: EventItem | null;
  onClearSelectedEvent?: () => void;
  onSelectEventItem?: (item: EventItem) => void;
  onRefreshEvents?: () => void;
}

export const EventsView: React.FC<EventsViewProps> = ({
  currentLang,
  events,
  selectedEventItem,
  onClearSelectedEvent,
  onSelectEventItem,
  onRefreshEvents,
}) => {
  const t = translations[currentLang];
  const [filterType, setFilterType] = useState<'upcoming' | 'past'>('upcoming');
  const [activeModalEvent, setActiveModalEvent] = useState<EventItem | null>(selectedEventItem || null);
  const [registered, setRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [regForm, setRegForm] = useState({ fullName: '', email: '', phone: '' });
  const [showRegInputs, setShowRegInputs] = useState(false);
  const [shareToast, setShareToast] = useState(false);

  const displayedEvents = events.filter((e) =>
    filterType === 'upcoming' ? !e.isPast : e.isPast
  );

  const handleShare = (titleText?: string) => {
    if (navigator.share) {
      navigator.share({
        title: titleText || t.eventsMainTitle,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(window.location.href);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 3000);
    }
  };

  const handleRegister = async () => {
    if (!activeModalEvent) return;
    if (!regForm.fullName || !regForm.email) {
      setShowRegInputs(true);
      return;
    }
    setRegistering(true);
    try {
      await registerEventApi(activeModalEvent.id, regForm.fullName, regForm.email, regForm.phone);
      setRegistered(true);
      if (onRefreshEvents) onRefreshEvents();
    } catch (err: any) {
      alert(err.message || 'Kayıt sırasında hata oluştu.');
    } finally {
      setRegistering(false);
    }
  };

  // Next & Prev Event logic
  const currentIndex = activeModalEvent ? displayedEvents.findIndex((e) => e.id === activeModalEvent.id) : -1;
  const handlePrevEvent = () => {
    if (currentIndex > 0) {
      const prev = displayedEvents[currentIndex - 1];
      setActiveModalEvent(prev);
      setRegistered(false);
      setShowRegInputs(false);
      if (onSelectEventItem) onSelectEventItem(prev);
    }
  };
  const handleNextEvent = () => {
    if (currentIndex >= 0 && currentIndex < displayedEvents.length - 1) {
      const next = displayedEvents[currentIndex + 1];
      setActiveModalEvent(next);
      setRegistered(false);
      setShowRegInputs(false);
      if (onSelectEventItem) onSelectEventItem(next);
    }
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Page Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <span className="px-3 py-1 rounded-full bg-[#132238] text-[#38BDF8] text-xs font-bold uppercase tracking-wider border border-[#38BDF8]/40 shadow-[0_0_10px_rgba(56,189,248,0.3)]">
          {t.eventsBadge}
        </span>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white font-heading">
          {t.eventsMainTitle}
        </h1>
        <p className="text-sm text-gray-300">
          {t.eventsSubtitle}
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex justify-center gap-3">
        <button
          onClick={() => setFilterType('upcoming')}
          className={`px-5 py-2.5 rounded-2xl text-xs font-bold transition-all ${
            filterType === 'upcoming'
              ? 'bg-[#38BDF8] text-[#0B1426] shadow-[0_0_15px_rgba(56,189,248,0.6)]'
              : 'bg-[#0B1426] text-gray-300 border border-[#38BDF8]/20 hover:bg-[#132238]'
          }`}
        >
          {t.upcomingEvents}
        </button>
        <button
          onClick={() => setFilterType('past')}
          className={`px-5 py-2.5 rounded-2xl text-xs font-bold transition-all ${
            filterType === 'past'
              ? 'bg-[#38BDF8] text-[#0B1426] shadow-[0_0_15px_rgba(56,189,248,0.6)]'
              : 'bg-[#0B1426] text-gray-300 border border-[#38BDF8]/20 hover:bg-[#132238]'
          }`}
        >
          {t.pastEvents}
        </button>
      </div>

      {/* Events List / Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {displayedEvents.map((evt) => (
          <div
            key={evt.id}
            className="glass-card rounded-3xl p-6 border border-[#38BDF8]/30 flex flex-col sm:flex-row gap-5 hover:border-[#38BDF8] hover:shadow-[0_0_20px_rgba(56,189,248,0.4)] transition-all duration-300 group"
          >
            {/* Event Poster & Date Badge */}
            <div className="relative w-full sm:w-48 h-48 rounded-2xl overflow-hidden flex-shrink-0">
              <img
                src={evt.image}
                alt={getLocalized(evt.title, currentLang)}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-2 left-2 px-3 py-1.5 rounded-2xl bg-[#0B1426]/90 border border-[#38BDF8] text-center shadow-xl backdrop-blur-md">
                <span className="block text-sm font-extrabold text-[#38BDF8]">
                  {evt.date.split('-')[2]}
                </span>
                <span className="block text-[10px] font-bold text-white uppercase">
                  {evt.date.split('-')[1]}
                </span>
              </div>
            </div>

            {/* Event Info */}
            <div className="flex-1 flex flex-col justify-between space-y-3">
              <div>
                <span className="px-2.5 py-1 rounded-full bg-[#0B1426] text-[#38BDF8] text-[10px] font-bold border border-[#38BDF8]/30">
                  {getLocalizedCategory(evt.category, currentLang)}
                </span>
                <h3 className="text-lg font-bold text-white mt-2 group-hover:text-[#38BDF8] transition-colors">
                  {getLocalized(evt.title, currentLang)}
                </h3>
                <p className="text-xs text-gray-300 line-clamp-2 mt-1 leading-relaxed">
                  {getLocalized(evt.description, currentLang)}
                </p>
              </div>

              <div className="space-y-1 text-xs text-gray-300 pt-3 border-t border-[#38BDF8]/15">
                <p className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-[#38BDF8]" />
                  <span>{evt.date} • {evt.time}</span>
                </p>
                <p className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#38BDF8]" />
                  <span className="truncate">{evt.location}</span>
                </p>
                <p className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-[#38BDF8]" />
                  <span>{t.eventCapacity}: {evt.registeredCount} / {evt.maxCapacity}</span>
                </p>
              </div>

              <button
                onClick={() => {
                  setRegistered(false);
                  setShowRegInputs(false);
                  setActiveModalEvent(evt);
                  if (onSelectEventItem) onSelectEventItem(evt);
                }}
                className="w-full py-2.5 rounded-xl bg-[#0B1426] border border-[#38BDF8] text-[#38BDF8] text-xs font-bold hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all flex items-center justify-center gap-2 shadow-[0_0_10px_rgba(56,189,248,0.2)]"
              >
                <QrCode className="w-4 h-4" />
                <span>{t.registerEvent}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Share Toast Banner */}
      {shareToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#38BDF8] text-[#0B1426] px-5 py-3 rounded-2xl font-extrabold text-xs shadow-[0_0_20px_rgba(56,189,248,0.8)] flex items-center gap-2 border border-white animate-bounce">
          <Share2 className="w-4 h-4 stroke-[3]" />
          <span>Bağlantı kopyalandı!</span>
        </div>
      )}

      {/* Full Event Detail Overlay */}
      {activeModalEvent && (
        <div className="fixed inset-0 z-50 bg-[#0B1426] text-white flex flex-col overflow-hidden animate-in fade-in duration-200">
          {/* Fixed Top Header */}
          <div className="sticky top-0 z-30 bg-[#0B1426]/95 backdrop-blur-md px-3 sm:px-6 py-3 border-b border-[#38BDF8]/40 shadow-[0_4px_20px_rgba(56,189,248,0.25)] flex items-center justify-between">
            {/* Left: Geri / İleri */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevEvent}
                disabled={currentIndex <= 0}
                className={`px-3 py-1.5 rounded-xl border border-[#38BDF8]/40 text-xs font-bold flex items-center gap-1 transition-all ${
                  currentIndex <= 0
                    ? 'opacity-40 cursor-not-allowed bg-[#132238] text-gray-400'
                    : 'bg-[#132238] text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] hover:shadow-[0_0_12px_rgba(56,189,248,0.6)]'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Geri</span>
              </button>

              <button
                onClick={handleNextEvent}
                disabled={currentIndex >= displayedEvents.length - 1}
                className={`px-3 py-1.5 rounded-xl border border-[#38BDF8]/40 text-xs font-bold flex items-center gap-1 transition-all ${
                  currentIndex >= displayedEvents.length - 1
                    ? 'opacity-40 cursor-not-allowed bg-[#132238] text-gray-400'
                    : 'bg-[#132238] text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] hover:shadow-[0_0_12px_rgba(56,189,248,0.6)]'
                }`}
              >
                <span className="hidden sm:inline">İleri</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Center: Category Badge */}
            <span className="px-3 py-1 rounded-full bg-[#132238] text-[#38BDF8] text-xs font-bold border border-[#38BDF8]/50 shadow-[0_0_10px_rgba(56,189,248,0.4)] truncate max-w-[150px] sm:max-w-xs">
              {getLocalizedCategory(activeModalEvent.category, currentLang)}
            </span>

            {/* Right: Kapat (Close) */}
            <button
              onClick={() => {
                setActiveModalEvent(null);
                if (onClearSelectedEvent) onClearSelectedEvent();
              }}
              className="px-3.5 py-1.5 rounded-full bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_15px_rgba(165,243,252,0.8)] transition-all flex items-center gap-1"
            >
              <span>{t.close || 'Kapat'}</span>
              <X className="w-4 h-4 stroke-[3]" />
            </button>
          </div>

          {/* Scrollable Content Body */}
          <div className="flex-1 overflow-y-auto px-2 sm:px-4 py-6 max-w-4xl mx-auto w-full space-y-6 scrollbar-thin">
            {/* Oval Glowing Image Frame */}
            {activeModalEvent.image && (
              <div className="relative w-full max-h-[380px] rounded-[2.5rem] overflow-hidden border-2 border-[#38BDF8] shadow-[0_0_25px_rgba(56,189,248,0.5)]">
                <img
                  src={activeModalEvent.image}
                  alt="Event Poster"
                  className="w-full h-full object-cover max-h-[380px]"
                />
              </div>
            )}

            {/* Title & Info */}
            <div className="space-y-3">
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white font-heading leading-tight">
                {getLocalized(activeModalEvent.title, currentLang)}
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-gray-200">
                <div className="p-3 rounded-2xl bg-[#132238] border border-[#38BDF8]/30 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#38BDF8]" />
                  <span>📅 {activeModalEvent.date} ({activeModalEvent.time})</span>
                </div>
                <div className="p-3 rounded-2xl bg-[#132238] border border-[#38BDF8]/30 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#38BDF8]" />
                  <span>📍 {activeModalEvent.location}</span>
                </div>
              </div>
            </div>

            {/* Event Description */}
            <div className="text-sm sm:text-base text-gray-200 leading-relaxed whitespace-pre-line py-2">
              {getLocalized(activeModalEvent.description, currentLang)}
            </div>

            {/* Location Address & Map Box */}
            <div className="rounded-2xl p-4 bg-[#132238] border border-[#38BDF8]/30 text-center space-y-2">
              <MapPin className="w-6 h-6 text-[#38BDF8] mx-auto animate-bounce" />
              <p className="text-xs font-bold text-white">{activeModalEvent.location}</p>
              <p className="text-[11px] text-gray-300">{activeModalEvent.address}</p>
            </div>

            {/* Registration Pass Generator or Actions */}
            {registered ? (
              <div className="p-6 rounded-3xl bg-[#132238] border-2 border-[#38BDF8] text-center space-y-4 shadow-[0_0_25px_rgba(56,189,248,0.5)]">
                <CheckCircle2 className="w-12 h-12 text-[#38BDF8] mx-auto animate-bounce" />
                <h3 className="text-lg font-bold text-white font-heading">KAYIT QR GEÇİŞ BİLETİ VERİTABANINA KAYDEDİLDİ</h3>
                <div className="p-4 bg-white text-black inline-block rounded-2xl shadow-xl">
                  <div className="w-32 h-32 border-4 border-black p-2 flex items-center justify-center font-mono text-[10px] font-bold text-center">
                    [QR PASS]
                    {activeModalEvent.id}
                  </div>
                </div>
                <p className="text-xs text-gray-300">Etkinlik girişinde bu QR kodu görevlilere okutabilirsiniz.</p>
                <button
                  onClick={() => alert('Giriş Bileti İndirildi!')}
                  className="px-5 py-2.5 rounded-2xl bg-[#38BDF8] text-[#0B1426] text-xs font-extrabold flex items-center gap-2 mx-auto shadow-[0_0_15px_rgba(56,189,248,0.6)]"
                >
                  <Download className="w-4 h-4" />
                  <span>Bileti İndir</span>
                </button>
              </div>
            ) : (
              <div className="p-4 rounded-2xl bg-[#132238] border border-[#38BDF8]/40 space-y-3">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-xs text-gray-300">
                    <p className="font-bold text-[#38BDF8]">Katılım Durumu</p>
                    <p>{activeModalEvent.registeredCount} kişi kaydoldu (Kapasite: {activeModalEvent.maxCapacity})</p>
                  </div>
                </div>

                {showRegInputs && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                    <input
                      type="text"
                      placeholder="Ad Soyad *"
                      value={regForm.fullName}
                      onChange={(e) => setRegForm({ ...regForm, fullName: e.target.value })}
                      className="p-2.5 rounded-xl bg-[#0B1426] border border-[#38BDF8]/40 text-white text-xs"
                    />
                    <input
                      type="email"
                      placeholder="E-Posta *"
                      value={regForm.email}
                      onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                      className="p-2.5 rounded-xl bg-[#0B1426] border border-[#38BDF8]/40 text-white text-xs"
                    />
                    <input
                      type="tel"
                      placeholder="Telefon"
                      value={regForm.phone}
                      onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })}
                      className="p-2.5 rounded-xl bg-[#0B1426] border border-[#38BDF8]/40 text-white text-xs"
                    />
                  </div>
                )}

                <button
                  onClick={handleRegister}
                  disabled={registering}
                  className="w-full py-3 rounded-2xl bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] transition-all shadow-[0_0_20px_rgba(56,189,248,0.6)] flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {registering ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <>
                      <QrCode className="w-4 h-4" />
                      <span>{showRegInputs ? 'Kaydı Onayla ve Veritabanına Ekle' : (t.registerEvent || 'Etkinliğe Kaydol')}</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Bottom Actions: Paylaş & Yazdır */}
            <div className="pt-8 pb-12 border-t border-[#38BDF8]/20 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleShare(getLocalized(activeModalEvent.title, currentLang))}
                  className="px-5 py-2.5 rounded-2xl bg-[#132238] border border-[#38BDF8] text-[#38BDF8] font-extrabold text-xs hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all shadow-[0_0_12px_rgba(56,189,248,0.4)] flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  <span>Paylaş</span>
                </button>

                <button
                  onClick={() => window.print()}
                  className="px-5 py-2.5 rounded-2xl bg-[#132238] border border-[#38BDF8]/50 text-gray-200 font-bold text-xs hover:bg-[#1C3352] hover:text-white transition-all flex items-center gap-2"
                >
                  <Printer className="w-4 h-4 text-[#38BDF8]" />
                  <span>Yazdır</span>
                </button>
              </div>

              <button
                onClick={() => {
                  setActiveModalEvent(null);
                  if (onClearSelectedEvent) onClearSelectedEvent();
                }}
                className="px-6 py-2.5 rounded-full bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_20px_rgba(165,243,252,0.8)] transition-all"
              >
                {t.close || 'Kapat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
